import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Input,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { DropdownItem } from '@shared/ui/dropdown/interfaces/dropdown-item';
import { createAvailableDropdownItemsObservable } from '@shared/ui/dropdown/helpers/create-available-dropdown-items';
import { isDropdownActionItem, isDropdownHelpTextItem } from '@shared/ui/dropdown/helpers/dropdown-type-guards';
import { DropdownTrigger } from '@shared/ui/dropdown/enumerators/dropdown-trigger';
import {
  DROPDOWN_CLICK_TRACKER,
  DropdownClickTracker
} from '@shared/ui/dropdown/injection-tokens/dropdown-click-tracker';
import { Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';

const ANIMATION_TRANSITION_IN = `0.2s cubic-bezier(0.23, 1, 0.32, 1)`;
const ANIMATION_TRANSITION_OUT = `0.2s cubic-bezier(0.755, 0.05, 0.855, 0.06)`;

// Fallback location reported for items that opt into central tracking via `trackingAction`
// only (without also setting a more specific `trackingLocation`).
const DEFAULT_DROPDOWN_CLICK_LOCATION = 'Dropdown';

@Component({
  selector: 'loop-dropdown-menu-renderer',
  templateUrl: './browser-menu-renderer.component.html',
  styleUrls: ['./browser-menu-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideMotion', [
      state(
        'void',
        style({
          opacity: 0,
          transform: 'scaleY(0.8)'
        })
      ),
      state(
        'enter',
        style({
          opacity: 1,
          transform: 'scaleY(1)'
        })
      ),
      transition('void => *', [animate(ANIMATION_TRANSITION_IN)]),
      transition('* => void', [animate(ANIMATION_TRANSITION_OUT)])
    ])
  ],
  standalone: false
})
export class BrowserDropdownRendererComponent {
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly elementRef: ElementRef = inject(ElementRef);
  private readonly dropdownClickTracker: DropdownClickTracker | null = inject(DROPDOWN_CLICK_TRACKER, {
    optional: true
  });

  @ViewChild('cdkMenuTpl') readonly templateRef!: TemplateRef<void>;

  @Input() set availableItems(items: DropdownItem[]) {
    this.setItems(items);
  }

  @Input() set triggerNested(triggerNested: DropdownTrigger) {
    this.trigger = triggerNested;
  }

  trigger?: DropdownTrigger;
  protected readonly DropdownTrigger_Context: DropdownTrigger.context = DropdownTrigger.context;

  availableItems$?: Observable<readonly DropdownItem[]>;
  waitingElements$?: Observable<readonly DropdownItem[]>;

  private initialAvailableElements$?: Observable<readonly DropdownItem[]>;

  onOpen(): void {
    if (this.waitingElements$) {
      this.availableItems$ = this.waitingElements$;
      this.waitingElements$ = undefined;
    }
  }

  setItems(dropdownItems: readonly DropdownItem[], options?: { isDropdownOpened: boolean }): void {
    if (!options?.isDropdownOpened) {
      this.availableItems$ = createAvailableDropdownItemsObservable(dropdownItems);
      this.initialAvailableElements$ = this.availableItems$;
    } else {
      this.waitingElements$ = createAvailableDropdownItemsObservable(dropdownItems);
    }
    this.cdr.detectChanges();
  }

  addItems(dropdownItemsOptions: { dropdownItems: readonly DropdownItem[]; shouldPrepend?: boolean }[]): void {
    // Items may be added before anything was ever set on the renderer, in which case there is simply
    // nothing to merge the added items with.
    this.availableItems$ = (this.initialAvailableElements$ ?? of<readonly DropdownItem[]>([])).pipe(
      take(1),
      map(availableItems => {
        let currentItems = availableItems;
        dropdownItemsOptions.forEach(dropdownItemsOption => {
          if (dropdownItemsOption.shouldPrepend) {
            currentItems = [...dropdownItemsOption.dropdownItems, ...currentItems];
          } else {
            currentItems = [...currentItems, ...dropdownItemsOption.dropdownItems];
          }
        });
        return currentItems;
      })
    );
    this.cdr.detectChanges();
  }

  handleItemClick(item: DropdownItem): void {
    if (isDropdownActionItem(item) && !isDropdownHelpTextItem(item) && item.action) {
      this.trackItemClick(item);

      item.action(this.elementRef);

      // On Electron the context menu is for some reason not closed and thus we need to manually trigger cdr
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 0);
    }
  }

  /**
   * Central choke-point instrumentation: items opt in by setting `trackingAction` and/or
   * `trackingLocation`. Items that set neither are left untracked here (e.g. because a
   * richer, manually-instrumented `trackUserClick` call downstream already covers them).
   */
  private trackItemClick(item: DropdownItem): void {
    if (!this.dropdownClickTracker || !(item.trackingAction || item.trackingLocation)) {
      return;
    }

    this.dropdownClickTracker.track(
      item.trackingLocation ?? DEFAULT_DROPDOWN_CLICK_LOCATION,
      item.trackingAction ?? item.title
    );
  }
}
